import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Slider,
  TableSortLabel,
  Tooltip,
  LinearProgress,
  Collapse,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as AnalyzeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  OpenInNew as OpenInNewIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockService } from '../../services/stockService';

const getScreenerSymbol = (symbol: string): string => {
  return symbol.replace('.NS', '').replace('.BO', '');
};

interface ValueStockScore {
  id: string;
  stock_id: string;
  symbol: string;
  stock_name: string;
  analysis_date: string;
  pe_ratio: number;
  pe_ratio_score: number;
  pb_ratio: number;
  pb_ratio_score: number;
  debt_equity_ratio: number;
  debt_equity_score: number;
  earnings_growth_3yr: number;
  earnings_growth_score: number;
  roe: number;
  roe_score: number;
  dividend_yield: number;
  dividend_yield_score: number;
  price_vs_200ma: number;
  price_vs_200ma_score: number;
  relative_strength: number;
  relative_strength_score: number;
  distance_from_52w_high: number;
  trend_score: number;
  death_cross_days: number;
  death_cross_score: number;
  fundamental_score: number;
  technical_score: number;
  combined_value_score: number;
  recommendation: string;
  sector: string;
}

type SortField = 'combined_value_score' | 'fundamental_score' | 'technical_score' | 'pe_ratio' | 'symbol';
type SortOrder = 'asc' | 'desc';

const ValueStocksTab: React.FC = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState<ValueStockScore[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>('');
  const [minScore, setMinScore] = useState<number>(50);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Favorites filter
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [watchlistCount, setWatchlistCount] = useState(0);
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('combined_value_score');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Expanded row for details
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useEffect(() => {
    loadValueStocks();
  }, [selectedSector, minScore, sortField, sortOrder, favoritesOnly]);

  // Load sectors from the stocks API
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await stockService.getSectors();
        if (response.sectors && response.sectors.length > 0) {
          setSectors(response.sectors);
        }
      } catch (e) {
        // silently fail - sectors just won't be available
      }
    };
    fetchSectors();
  }, []);

  const loadValueStocks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (favoritesOnly) {
        response = await stockService.getValueStocksFavorites({
          min_score: minScore,
          sort_by: sortField,
          sort_order: sortOrder,
        });
        setWatchlistCount(response.watchlist_count || 0);
        // Filter by sector client-side for favorites
        let filteredStocks = response.stocks || [];
        if (selectedSector) {
          filteredStocks = filteredStocks.filter((s: ValueStockScore) => s.sector === selectedSector);
        }
        setStocks(filteredStocks);
        // Get sectors from filtered stocks
        const sectorSet = new Set<string>();
        (response.stocks || []).forEach((s: ValueStockScore) => {
          if (s.sector) sectorSet.add(s.sector);
        });
        setSectors(Array.from(sectorSet).sort());
      } else {
        response = await stockService.getValueStocks({
          min_score: minScore,
          limit: 200,
          sector: selectedSector || undefined,
          sort_by: sortField,
          sort_order: sortOrder,
        });
        setStocks(response.stocks || []);
        setSectors(response.sectors || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load value stocks');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAnalysis = async () => {
    try {
      setAnalyzing(true);
      
      // Trigger analysis - favorites only or all stocks
      let response;
      if (favoritesOnly) {
        response = await fetch('/api/strategies/value-stocks/analyze/favorites', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } else {
        response = await fetch('/api/strategies/value-stocks/analyze', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }
      
      if (response.ok) {
        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch('/api/strategies/analysis/status', {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const status = await statusResponse.json();
            
            if (!status.value_analysis_running) {
              clearInterval(pollInterval);
              setAnalyzing(false);
              loadValueStocks();
            }
          } catch (e) {
            clearInterval(pollInterval);
            setAnalyzing(false);
          }
        }, 5000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start analysis');
      setAnalyzing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'pe_ratio' ? 'asc' : 'desc');
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 75) return '#4caf50';
    if (score >= 55) return '#8bc34a';
    if (score >= 35) return '#ff9800';
    return '#f44336';
  };

  const getRecommendationColor = (rec: string): "success" | "warning" | "default" | "error" => {
    switch (rec?.toLowerCase()) {
      case 'strong_buy': return 'success';
      case 'buy': return 'success';
      case 'hold': return 'warning';
      case 'avoid': return 'error';
      default: return 'default';
    }
  };

  const formatRecommendation = (rec: string): string => {
    return rec?.replace('_', ' ').toUpperCase() || 'N/A';
  };

  const renderScoreBar = (score: number, max: number, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ width: 80, fontSize: '0.7rem' }}>{label}</Typography>
      <LinearProgress
        variant="determinate"
        value={(score / max) * 100}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: getScoreColor((score / max) * 100),
            borderRadius: 3,
          }
        }}
      />
      <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontSize: '0.7rem' }}>
        {score.toFixed(0)}/{max}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {/* Filters and Controls */}
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={0.5} alignItems="center">
                <FormControl fullWidth size="small">
                  <InputLabel>Sector</InputLabel>
                  <Select
                    value={selectedSector}
                    onChange={(e) => setSelectedSector(e.target.value)}
                    label="Sector"
                  >
                    <MenuItem value="">All Sectors</MenuItem>
                    {sectors.map((sector) => (
                      <MenuItem key={sector} value={sector}>{sector}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {sectors.length === 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      try {
                        await stockService.fetchSectors(true);
                        toast.info('Fetching sectors from Yahoo Finance...');
                      } catch (e) {
                        toast.error('Failed to start sector fetch');
                      }
                    }}
                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto', fontSize: '0.7rem' }}
                  >
                    Fetch
                  </Button>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="caption" gutterBottom display="block">
                Minimum Score: {minScore}
              </Typography>
              <Slider
                value={minScore}
                onChange={(_, value) => setMinScore(value as number)}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 35, label: '35' },
                  { value: 55, label: '55' },
                  { value: 75, label: '75' },
                  { value: 100, label: '100' },
                ]}
                valueLabelDisplay="auto"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={favoritesOnly}
                    onChange={(e) => setFavoritesOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {favoritesOnly ? (
                      <StarIcon sx={{ fontSize: 16, color: 'gold' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                    )}
                    <span>Favorites</span>
                    {favoritesOnly && watchlistCount > 0 && (
                      <Chip label={watchlistCount} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                }
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={analyzing ? <CircularProgress size={16} /> : <AnalyzeIcon />}
                  onClick={handleTriggerAnalysis}
                  disabled={analyzing}
                  size="small"
                >
                  {analyzing ? 'Analyzing...' : favoritesOnly ? 'Analyze Favorites' : 'Run Analysis'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadValueStocks}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
                
                <IconButton
                  size="small"
                  onClick={() => setHelpOpen(true)}
                  title="How value scores are calculated"
                  sx={{ ml: 0.5 }}
                >
                  <HelpIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="Strong Buy (≥75)" size="small" sx={{ bgcolor: '#4caf50', color: 'white' }} />
            <Chip label="Buy (55-74)" size="small" sx={{ bgcolor: '#8bc34a', color: 'white' }} />
            <Chip label="Hold (35-54)" size="small" sx={{ bgcolor: '#ff9800', color: 'white' }} />
            <Chip label="Avoid (<35)" size="small" sx={{ bgcolor: '#f44336', color: 'white' }} />
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : stocks.length > 0 ? (
        <TableContainer component={Paper} sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.75, fontSize: '0.8rem' } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}></TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'symbol'}
                    direction={sortField === 'symbol' ? sortOrder : 'asc'}
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol
                  </TableSortLabel>
                </TableCell>
                <TableCell>Sector</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'pe_ratio'}
                    direction={sortField === 'pe_ratio' ? sortOrder : 'asc'}
                    onClick={() => handleSort('pe_ratio')}
                  >
                    P/E
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">P/B</TableCell>
                <TableCell align="right">ROE %</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'fundamental_score'}
                    direction={sortField === 'fundamental_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('fundamental_score')}
                  >
                    Fundamental
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'technical_score'}
                    direction={sortField === 'technical_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('technical_score')}
                  >
                    Technical
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'combined_value_score'}
                    direction={sortField === 'combined_value_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('combined_value_score')}
                  >
                    Combined
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Recommendation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.map((stock) => (
                <React.Fragment key={stock.id}>
                  <TableRow hover onClick={() => setExpandedRow(expandedRow === stock.id ? null : stock.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton size="small">
                        {expandedRow === stock.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {stock.combined_value_score >= 75 && (
                          <StarIcon sx={{ fontSize: 14, color: 'gold' }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/analysis?symbol=${encodeURIComponent(stock.symbol)}`); }}
                        >
                          {stock.symbol}
                        </Typography>
                        <a
                          href={`https://www.screener.in/company/${getScreenerSymbol(stock.symbol)}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center' }}
                          title="View on Screener.in"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
                        </a>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {stock.sector || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {stock.pe_ratio?.toFixed(1) || '-'}
                    </TableCell>
                    <TableCell align="right">
                      {stock.pb_ratio?.toFixed(2) || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {stock.roe && stock.roe >= 15 ? (
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        ) : stock.roe && stock.roe < 10 ? (
                          <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        ) : null}
                        {stock.roe?.toFixed(1) || '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={stock.fundamental_score?.toFixed(0)}
                        size="small"
                        sx={{
                          bgcolor: getScoreColor(stock.fundamental_score),
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 40,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={stock.technical_score?.toFixed(0)}
                        size="small"
                        sx={{
                          bgcolor: getScoreColor(stock.technical_score),
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 40,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={stock.combined_value_score?.toFixed(0)}
                        size="small"
                        sx={{
                          bgcolor: getScoreColor(stock.combined_value_score),
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 50,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatRecommendation(stock.recommendation)}
                        size="small"
                        color={getRecommendationColor(stock.recommendation)}
                      />
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row with Score Breakdown */}
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0 }}>
                      <Collapse in={expandedRow === stock.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1, px: 1.5 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Fundamental Scores</Typography>
                              {renderScoreBar(stock.pe_ratio_score || 0, 20, 'P/E Ratio')}
                              {renderScoreBar(stock.pb_ratio_score || 0, 15, 'P/B Ratio')}
                              {renderScoreBar(stock.debt_equity_score || 0, 15, 'D/E Ratio')}
                              {renderScoreBar(stock.earnings_growth_score || 0, 20, 'Earnings Growth')}
                              {renderScoreBar(stock.roe_score || 0, 15, 'ROE')}
                              {renderScoreBar(stock.dividend_yield_score || 0, 15, 'Dividend')}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Technical Scores</Typography>
                              {renderScoreBar(stock.price_vs_200ma_score || 0, 25, 'vs 200 MA')}
                              {renderScoreBar(stock.relative_strength_score || 0, 25, 'RS Rating')}
                              {renderScoreBar(stock.trend_score || 0, 25, 'Trend')}
                              {renderScoreBar(stock.death_cross_score || 0, 25, 'Death Cross')}
                              
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Price vs 200MA: {stock.price_vs_200ma?.toFixed(1)}% | 
                                  RS: {stock.relative_strength?.toFixed(0) || '-'} | 
                                  From 52W High: {stock.distance_from_52w_high?.toFixed(1)}%
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No value stocks found matching your criteria. Try lowering the minimum score.
        </Alert>
      )}
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Showing {stocks.length} stocks. Score breakdown: 60% fundamental metrics, 40% technical confirmation.
      </Typography>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Value Stocks — Scoring Methodology</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
            Fundamental Score (60% of combined, max 100 points)
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>P/E Ratio (0–20 pts)</strong> — Lower P/E relative to sector median = better value. &lt;10 scores highest, &gt;40 scores 0</li>
              <li><strong>P/B Ratio (0–15 pts)</strong> — Lower Price-to-Book = cheaper stock. &lt;1 scores highest, &gt;5 scores 0</li>
              <li><strong>Debt/Equity (0–15 pts)</strong> — Lower leverage = safer. D/E &lt;0.3 scores highest, &gt;2 scores 0</li>
              <li><strong>Earnings Growth 3yr (0–20 pts)</strong> — Higher CAGR = better growth. &gt;25% scores highest, negative scores 0</li>
              <li><strong>ROE (0–15 pts)</strong> — Return on equity. &gt;20% scores highest, &lt;5% scores 0</li>
              <li><strong>Dividend Yield (0–15 pts)</strong> — Reasonable yield is good. 2–5% optimal, &gt;10% may signal trouble</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Technical Score (40% of combined, max 100 points)
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Price vs 200 MA (0–25 pts)</strong> — Above 200-day MA confirms uptrend. 0–10% above is optimal</li>
              <li><strong>Relative Strength (0–25 pts)</strong> — RS rating vs market. &gt;70 scores highest</li>
              <li><strong>Trend / Distance from 52W High (0–25 pts)</strong> — Within 10% of high = strong momentum</li>
              <li><strong>Death Cross (0–25 pts)</strong> — No recent 50/200 MA bearish crossover. &gt;180 days or none = full score</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Recommendations
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Strong Buy ≥ 75</strong> — Undervalued with strong technicals</li>
              <li><strong>Buy 55–74</strong> — Good value with decent confirmation</li>
              <li><strong>Hold 35–54</strong> — Fair value or mixed signals</li>
              <li><strong>Avoid &lt; 35</strong> — Overvalued or in downtrend</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Sectors
          </Typography>
          <Typography variant="body2" paragraph>
            Sector data is sourced from <strong>Yahoo Finance</strong> (yfinance). Coverage includes: Basic Materials,
            Communication Services, Consumer Cyclical, Consumer Defensive, Energy, Financial Services, Healthcare,
            Industrials, Real Estate, Technology, and Utilities. Not all stocks have sector data — use the "Fetch" button
            to pull missing sectors from Yahoo Finance. The P/E score is compared against the sector median when available
            for more accurate relative valuation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValueStocksTab;
